import {
  isVisible
} from './utils.js'


let enableConsoleLogs = false
let logger = (...msg) => enableConsoleLogs && console.log('%cFormValidator:', 'color:pink;', ...msg)
let loggerError = (...msg) => enableConsoleLogs && console.error('%cFormValidator:', 'color:red;', ...msg)


/*
  * A client-side validator that makes use of the native HTMLFormElement API where possible.
  *
  * Handy references for how this validation setup works:
  * https://css-tricks.com/form-validation-part-2-constraint-validation-api-javascript/
  * https://github.com/jorgemanrubia/rails-form-validations-example/blob/master/app/javascript/controllers/form_controller.js
  *
  * Considerations when applying inline validation:
  * https://baymard.com/blog/inline-form-validation
  * https://alistapart.com/article/inline-validation-in-web-forms/
  */


export default class FormValidator {
  constructor(formElement) {
    logger(this)

    this.element = formElement

    // Turn off browser’s native validation so it doesn’t conflict with this script:
    this.element.setAttribute('novalidate', true)

    // Initial inline validation should only happen on blur or submit:
    this.element.addEventListener('blur', this.onBlur.bind(this), true)
    this.element.addEventListener('submit', this.onSubmit.bind(this), false)

    /*
      * Inline validation while someone’s making changes to a field should only happen
      * _after_ the first modification/validation is made to that field, because it’s
      * confusing/annoying to have it tell you you’re wrong before you’re done typing.
      * These event listener callbacks are set up to confirm if the field is dirty
      * before moving on to validation.
      */
    this.element.addEventListener('keyup', this.checkDirtyField.bind(this))
    this.element.addEventListener('change', this.checkDirtyField.bind(this))

    /*
      * The `change` event won’t fire unless someone interacts directly with an element
      * that can fire off a `change` event, and therefore it won’t happen at all with
      * a select element enhanced into an autocomplete, so listen to its custom change
      * event instead to validate its contents.
      */
    document.addEventListener('autocompletechange', (event) => {
      this.checkDirtyField(event)
    })
  }

  onBlur(event) {
    this.validateField(event.target)
  }

  checkDirtyField(event) {
    if (!this.isFieldDirty(event.target)) return
    this.validateField(event.target)
  }

  onSubmit(event) {
    /*
      * Fire preventDefault() outside of the validation check, or else the form will
      * go through no matter what if the validation check itself has any errors.
      */
    event.preventDefault()

    if (!this.validateForm()) {
      this.firstInvalidField.focus()
    } else {
      this.element.submit()
    }
  }

  validateForm() {
    let isValid = true
    this.formFields.forEach((field) => {
      if (!this.validateField(field)) isValid = false
    })
    return isValid
  }

  // Determines whether a form field's state is valid.
  validateField(fieldElement) {
    let field

    // 1. Is the field enhanced in a way that requires explicit selection of original form control?
    if (fieldElement.hasAttribute('aria-autocomplete')) {
      /*
        * For a <select> form control enhanced as an autocomplete, the blur event will instead
        * fire on an input element serving up its options as a combobox, or sometimes on an <li>
        * behaving as an option.
        *
        * Make sure checkValidity() fires on the <select> form control itself, instead of any
        * of the enhancements:
        */
      field = document.getElementById(`${fieldElement.id}-select`)
    } else {
      field = fieldElement
    }

    // 2. Is the field an actual form control, and does it belong to this instance's form?
    //    If not, get outta here
    if (!this.shouldValidateField(field)) return true

    // 3. Does the field need a custom validator, or is it possible to use the native checkValidity() method?
    let isValid

    switch (this.fieldType(field)) {
      case 'radio':
        isValid = this.customValidators.radio.validate(field)
        break
      case 'boundedMultiCheckbox':
        isValid = this.customValidators.boundedMultiCheckbox.validate(field)
        break
      default:
        isValid = field.checkValidity()
    }

    if (this.fieldContainer(field)) this.fieldContainer(field).setAttribute('data-field-valid', isValid)

    this.refreshErrorForInvalidField(field, isValid)
    return isValid
  }

  // Determines whether a field is a legitimate form control that should be validated.
  shouldValidateField(field) {
    return this.formFields.includes(field)
      && ['file', 'reset', 'submit', 'button'].indexOf(field.type) === -1
      && isVisible(field) || field.hasAttribute('data-autocomplete-target')
      && !field.disabled
  }

  // Determines whether a field has previously been interacted with.
  isFieldDirty(field) {
    if (!this.shouldValidateField(field)) return false
    /*
      * A field’s container will get the attribute [data-field-valid] after the
      * first time it’s blurred, and we don’t want to harangue someone prior to
      * a field being blurred, so that attribute is all we need to check for.
      */
    return this.fieldContainer(field).hasAttribute('data-field-valid')
  }

  // Handles whether to show or remove error messages for a form field.
  refreshErrorForInvalidField(field, isValid) {
    this.removeExistingErrorMessage(field)
    if (!isValid) this.showErrorForInvalidField(field)
  }

  removeExistingErrorMessage(field) {
    if (!this.fieldContainer(field)) return

    let existingErrorMessageElement = this.fieldContainer(field).querySelector('[data-field-error]')
    if (existingErrorMessageElement)
      existingErrorMessageElement.parentNode.removeChild(existingErrorMessageElement)
  }

  showErrorForInvalidField(field) {
    let errorContainer = this.fieldContainer(field).querySelector('[data-error-container]')

    errorContainer.insertAdjacentHTML('beforeend', this.buildFieldErrorHtml(field))
  }

  buildFieldErrorHtml(field) {
    // If there is no custom error message for the field, use the browser’s built-in message:
    let errorMessage = field.getAttribute('data-error-message') || field.validationMessage

    return `<p class="form-error" data-field-error>
      <svg class="icon" style="fill:currentColor" width="18" height="18" role="img" aria-hidden="true">
        <use href="#icon-info" xlink:href="#icon-info" />
      </svg>
      ${errorMessage}
    </p>`
  }

  // Finds the field container element for a given form field.
  fieldContainer(field) {
    let container = field.closest('[data-field-container]')
    if (!container) {
      loggerError('Can’t find a parent [data-field-container] for', field)
    }
    return container
  }

  // Identifies a form field as a custom field type for validation.
  fieldType(field) {
    let identifiedType = Object.entries(this.customValidators).find(([typeName, condition]) => {
      return condition.match(field) ? typeName : false
    })

    return identifiedType ? identifiedType[0] : undefined
  }

  customValidators = {
    // Identifies/validates a group of checkboxes that requires a minimum and/or maximum number of its members checked.
    boundedMultiCheckbox: {
      match: (f) => {
        return f.type === 'checkbox'
          && (
            f.dataset.min || this.fieldContainer(f).dataset.min
            || f.dataset.max || this.fieldContainer(f).dataset.max
          )
      },
      validate: (field) => {
        // Get min and/or max values:
        let min = parseInt(field.dataset.min || this.fieldContainer(field).dataset.min)
        let max = parseInt(field.dataset.max || this.fieldContainer(field).dataset.max)

        // Get group of checkboxes of the same name as the supplied field:
        let checkboxGroup = this.formFields.filter(f => f.name === field.name)
        let checkedCount = checkboxGroup.filter(f => f.checked).length

        if (min && !max) {
          return checkedCount >= min
        }
        if (!min && max) {
          return checkedCount <= max
        }
        if (min && max) {
          return checkedCount >= min && checkedCount <= max
        }
      },
    },

    // Identifies/validates a radio group.
    radio: {
      match: (field) => field.type === 'radio',
      validate: (field) => {
        /*
          * [role="radiogroup"] and [aria-required="true"] are applied to <fieldset> instead of
          * applying [required] directly to the radio input elements, following Adrian Roselli's
          * findings and recommendations:
          * https://adrianroselli.com/2022/02/support-for-marking-radio-buttons-required-invalid.html
          */
        let radioGroup = this.formFields.filter(f => f.name === field.name)
        let checkedCount = radioGroup.filter(f => f.checked).length
        return checkedCount > 0
      },
    },
  }

  get formFields() {
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/elements
    return Array.prototype.slice.call(this.element.elements)
  }

  get firstInvalidField() {
    return this.formFields.find(field => !this.validateField(field))
  }
}
